<template lang="pug">
.mis-recs
  h1.mr-title Recomendaciones

  .mr-loading(v-if="loading")
    .mr-spinner
    span Cargando…

  template(v-else)
    section.mr-section
      h2 Información relevante
      p.mr-desc Contenidos sobre tu tratamiento y cuidados, y novedades del centro.
      .mr-empty(v-if="!recInfo.length") Todavía no hay contenidos para mostrar.
      .mr-grid
        .mr-card(v-for="r in recInfo" :key="r._id" @click="openRec(r)")
          .mr-cover(:class="categoryCover(r.category)")
            span.mr-tag {{ categoryLabel(r.category) }}
            span.mr-cover__ico(v-html="icon(categoryIcon(r.category))")
          .mr-body
            h3 {{ taskName(r) }}
            p(v-if="r.description") {{ r.description }}
            span.mr-read
              | Leer
              span(v-html="icon('chevron')")

    section.mr-section
      h2 Campañas
      p.mr-desc Campañas de salud y acciones de prevención vigentes.
      .mr-empty(v-if="!recCamp.length") No hay campañas activas por ahora.
      .mr-grid
        .mr-card(v-for="r in recCamp" :key="r._id" @click="openRec(r)")
          .mr-cover.cover-camp
            span.mr-tag {{ categoryLabel(r.category) }}
            span.mr-cover__ico(v-html="icon('campaign')")
          .mr-body
            h3 {{ taskName(r) }}
            p(v-if="r.description") {{ r.description }}
            span.mr-read
              | Leer
              span(v-html="icon('chevron')")

  //- Drawer de lectura
  .mr-scrim(v-show="drawer.visible" @click="closeDrawer")
  aside.mr-drawer(:class="{ 'is-open': drawer.visible }")
    .mr-d(v-if="drawer.task")
      .mr-d__head
        .mr-d__ico(:class="categoryCover(drawer.task.category)" v-html="icon(categoryIcon(drawer.task.category))")
        div
          h3 {{ taskName(drawer.task) }}
          .mr-d__sub Recomendación · {{ categoryLabel(drawer.task.category) }}
        button.mr-d__close(@click="closeDrawer") ✕
      .mr-d__body(v-html="drawer.contentHtml")
      .mr-d__foot
        button.mr-btn(@click="closeDrawer") Entendido
</template>

<script>
import patientTaskService from "@/services/patient-task";

const ICONS = {
  chevron: '<path d="M9 18l6-6-6-6"/>',
  campaign:
    '<path d="M3 11l18-5v12L3 14v-3z"/><path d="M11.6 16.8a3 3 0 0 1-5.8-1.6"/>',
  hospital:
    '<rect x="3" y="4" width="18" height="16" rx="2"/><path d="M12 8v6M9 11h6"/>',
  building:
    '<rect x="4" y="3" width="16" height="18" rx="1"/><path d="M9 21v-4h6v4M8 7h.01M12 7h.01M16 7h.01M8 11h.01M12 11h.01M16 11h.01"/>',
  bulb: '<path d="M9 18h6M10 22h4M12 2a7 7 0 0 0-4 12c.7.7 1 1.5 1 2h6c0-.5.3-1.3 1-2A7 7 0 0 0 12 2z"/>',
  info: '<circle cx="12" cy="12" r="9"/><path d="M12 8h.01M11 12h1v4h1"/>',
};

const CATEGORY = {
  category1: { label: "Patología", cover: "cover-pat", icon: "hospital" },
  category2: { label: "Campaña", cover: "cover-camp", icon: "campaign" },
  category3: { label: "Institucional", cover: "cover-inst", icon: "building" },
  category4: { label: "Tips de salud", cover: "cover-tip", icon: "bulb" },
};

/**
 * @component Recomendaciones
 * @description Sección del portal del paciente con las recomendaciones,
 *   separadas en "Información relevante" (Patología / Institucional / Tips) y
 *   "Campañas" (categoría campañas). Cada card abre un drawer de lectura con el
 *   contenido real (imagen / video / YouTube embebido / texto) + links.
 * @example <Recomendaciones />
 */
export default {
  name: "Recomendaciones",
  data() {
    return {
      loading: true,
      recs: [],
      drawer: { visible: false, task: null, contentHtml: "" },
    };
  },
  computed: {
    recInfo() {
      return this.recs.filter((r) => r.category !== "category2");
    },
    recCamp() {
      return this.recs.filter((r) => r.category === "category2");
    },
  },
  created() {
    this.fetch();
  },
  methods: {
    async fetch() {
      try {
        const raw = await patientTaskService.getMyRecommendations();
        // Cada recomendación tiene varias instancias agendadas (una por fecha).
        // Mostramos cada contenido UNA sola vez: dedup por `parentTask` (la
        // plantilla de la que se clonan) o, si no está, por nombre + categoría.
        const byKey = {};
        (Array.isArray(raw) ? raw : []).forEach((r) => {
          const key = r.parentTask
            ? String(r.parentTask)
            : `${r.name || ""}|${r.category || ""}`;
          if (!byKey[key]) byKey[key] = r;
        });
        this.recs = Object.values(byKey);
      } catch (err) {
        this.recs = [];
      } finally {
        this.loading = false;
      }
    },
    icon(name) {
      return (
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
        'stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">' +
        (ICONS[name] || "") +
        "</svg>"
      );
    },
    taskName(t) {
      return t && t.name ? t.name : "Recomendación";
    },
    categoryLabel(cat) {
      return CATEGORY[cat] ? CATEGORY[cat].label : "Recomendación";
    },
    categoryCover(cat) {
      return CATEGORY[cat] ? CATEGORY[cat].cover : "cover-inst";
    },
    categoryIcon(cat) {
      return CATEGORY[cat] ? CATEGORY[cat].icon : "info";
    },
    async openRec(r) {
      const raw = this.mediaRaw(r);
      const httpUrl = /^https?:\/\//i.test(raw) ? raw : null;
      // Contenido base (texto/descripción/YouTube) — sin la media que aún no
      // sabemos si existe.
      this.drawer = {
        visible: true,
        task: r,
        contentHtml: this.recContentHtml(r, httpUrl),
      };
      // Resolver la media: signed URL si es una key privada.
      let url = httpUrl;
      if (raw && !httpUrl) {
        url = await patientTaskService.getRecommendationMediaUrl(r._id);
      }
      if (!this.drawer.task || this.drawer.task._id !== r._id) return;
      // Para imagen/video (no YouTube), precargar para confirmar que existe:
      // si el archivo no está (ej. dato de seed sin subir), evitamos el hueco.
      let finalUrl = url;
      const type = (r.media && r.media.type) || "";
      if (
        url &&
        (type === "image" || type === "video") &&
        !this.youtubeId(url)
      ) {
        const ok = await this.mediaLoads(url, type);
        if (!this.drawer.task || this.drawer.task._id !== r._id) return;
        finalUrl = ok ? url : null;
      }
      this.$set(this.drawer, "contentHtml", this.recContentHtml(r, finalUrl));
    },
    /**
     * @description Precarga una imagen/video para saber si el archivo existe
     *              realmente (la signed URL puede apuntar a un objeto ausente).
     * @param {string} url
     * @param {string} type - 'image' | 'video'
     * @returns {Promise<boolean>}
     */
    mediaLoads(url, type) {
      return new Promise((resolve) => {
        let done = false;
        const finish = (ok) => {
          if (!done) {
            done = true;
            resolve(ok);
          }
        };
        if (type === "image") {
          const img = new Image();
          img.onload = () => finish(true);
          img.onerror = () => finish(false);
          img.src = url;
        } else {
          finish(true);
        }
        setTimeout(() => finish(false), 6000);
      });
    },
    closeDrawer() {
      this.drawer.visible = false;
    },
    mediaRaw(task) {
      const m = task && task.media;
      if (!m) return "";
      if (typeof m.content === "string") return m.content;
      if (Array.isArray(m.content) && m.content[0]) {
        const c = m.content[0];
        return (
          (c && (c.value || c.url)) || (typeof c === "string" ? c : "") || ""
        );
      }
      return m.url || m.value || "";
    },
    youtubeId(url) {
      if (!url) return null;
      const m = String(url).match(
        /(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|v\/|shorts\/))([\w-]{11})/
      );
      return m ? m[1] : null;
    },
    /**
     * @description HTML del contenido (media/YouTube/texto + descripción + links).
     * @param {Object} task
     * @param {string|null} [resolvedUrl] - URL ya resuelta (signed o YouTube).
     * @returns {string}
     */
    recContentHtml(task, resolvedUrl) {
      const esc = (s) =>
        String(s == null ? "" : s).replace(
          /[&<>"]/g,
          (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c])
        );
      const m = (task && task.media) || {};
      const raw = this.mediaRaw(task);
      const url = resolvedUrl || (/^https?:\/\//i.test(raw) ? raw : "");
      const type = m.type || "";
      const yid = this.youtubeId(url);
      const parts = [];
      if (yid) {
        parts.push(
          '<div class="mr-embed"><iframe src="https://www.youtube.com/embed/' +
            yid +
            '" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe></div>'
        );
      } else if (type === "video" && url) {
        parts.push(
          '<video class="mr-media-el" controls src="' +
            esc(url) +
            '" onerror="this.style.display=\'none\'"></video>'
        );
      } else if (type === "image" && url) {
        parts.push(
          '<img class="mr-media-el" src="' +
            esc(url) +
            '" alt="" onerror="this.style.display=\'none\'" />'
        );
      } else if (type === "text" && url) {
        parts.push('<p class="mr-rtext">' + esc(url) + "</p>");
      }
      if (task && task.description) {
        parts.push('<p class="mr-rtext">' + esc(task.description) + "</p>");
      }
      if (task && Array.isArray(task.links)) {
        task.links.forEach((l) => {
          if (l && l.url) {
            parts.push(
              '<a class="mr-link" href="' +
                esc(l.url) +
                '" target="_blank" rel="noopener">' +
                esc(l.name || l.url) +
                "</a>"
            );
          }
        });
      }
      if (!parts.length) {
        parts.push(
          '<div class="mr-empty-content">Esta recomendación todavía no tiene contenido para mostrar.</div>'
        );
      }
      return parts.join("");
    },
  },
};
</script>

<style lang="scss" scoped>
$ink: #1b2233;
$muted: #697392;
$faint: #98a2bd;
$surface: #fff;
$border: #e6eaf3;
$teal: #12a594;

.mis-recs {
  font-family: "Plus Jakarta Sans", system-ui, -apple-system, Segoe UI, Roboto,
    sans-serif;
  color: $ink;
  padding: 4px 2px 40px;
}
.mr-title {
  font-size: 20px;
  font-weight: 700;
  letter-spacing: -0.02em;
  margin: 0 0 18px;
  color: #0f172a;
}
.mr-loading {
  display: flex;
  align-items: center;
  gap: 12px;
  color: $muted;
  font-size: 13.5px;
  padding: 30px 4px;
}
.mr-spinner {
  width: 20px;
  height: 20px;
  border: 3px solid $border;
  border-top-color: $teal;
  border-radius: 50%;
  animation: mr-spin 0.8s linear infinite;
}
@keyframes mr-spin {
  to {
    transform: rotate(360deg);
  }
}
.mr-section {
  margin-bottom: 26px;
}
.mr-section > h2 {
  font-size: 15px;
  font-weight: 700;
  letter-spacing: -0.01em;
  margin: 0 2px 3px;
  color: #0f172a;
}
.mr-desc {
  color: $muted;
  font-size: 12.5px;
  margin: 0 2px 14px;
}
.mr-empty {
  color: $faint;
  font-size: 13px;
  font-style: italic;
  padding: 6px 2px;
}
.mr-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(210px, 1fr));
  gap: 14px;
}
.mr-card {
  background: $surface;
  border: 1px solid $border;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(27, 34, 51, 0.05),
    0 8px 22px rgba(27, 34, 51, 0.06);
  cursor: pointer;
  transition: transform 0.18s, box-shadow 0.18s;
  display: flex;
  flex-direction: column;
}
.mr-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 16px 40px rgba(20, 28, 54, 0.14);
}
.mr-cover {
  height: 90px;
  display: grid;
  place-items: center;
  position: relative;
  color: #fff;
}
.mr-cover__ico ::v-deep svg {
  width: 30px;
  height: 30px;
  opacity: 0.95;
}
/* Covers planos (sin degradé), familia fría navy + teal del producto. */
.cover-pat {
  background: #3d54b0; // indigo
}
.cover-camp {
  background: #0e8578; // teal profundo
}
.cover-inst {
  background: #64748b; // slate
}
.cover-tip {
  background: #2e9e6c; // verde
}
.mr-tag {
  position: absolute;
  top: 10px;
  left: 10px;
  background: rgba(255, 255, 255, 0.9);
  color: #333;
  font-size: 10px;
  font-weight: 700;
  padding: 2px 9px;
  border-radius: 999px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}
.mr-body {
  padding: 12px 14px 14px;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 5px;
}
.mr-body h3 {
  font-size: 13.5px;
  font-weight: 600;
  margin: 0;
  letter-spacing: -0.01em;
  color: #0f172a;
}
.mr-body p {
  margin: 0;
  color: $muted;
  font-size: 12px;
  line-height: 1.5;
}
.mr-read {
  margin-top: auto;
  padding-top: 6px;
  color: $teal;
  font-weight: 600;
  font-size: 12.5px;
  display: inline-flex;
  align-items: center;
  gap: 5px;
}
.mr-read ::v-deep svg {
  width: 14px;
  height: 14px;
}

/* drawer */
.mr-scrim {
  position: fixed;
  inset: 0;
  background: rgba(16, 22, 40, 0.44);
  z-index: 2000;
}
.mr-drawer {
  position: fixed;
  top: 0;
  right: 0;
  height: 100%;
  width: min(430px, 94vw);
  background: $surface;
  box-shadow: -12px 0 40px rgba(20, 28, 54, 0.18);
  transform: translateX(100%);
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 2001;
  display: flex;
  flex-direction: column;
}
.mr-drawer.is-open {
  transform: none;
}
.mr-d {
  display: flex;
  flex-direction: column;
  height: 100%;
}
.mr-d__head {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 20px 20px 16px;
  border-bottom: 1px solid $border;
}
.mr-d__ico {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  display: grid;
  place-items: center;
  color: #fff;
  flex: none;
}
.mr-d__ico ::v-deep svg {
  width: 20px;
  height: 20px;
}
.mr-d__head h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: #0f172a;
}
.mr-d__sub {
  color: $muted;
  font-size: 12.5px;
  margin-top: 2px;
}
.mr-d__close {
  margin-left: auto;
  background: #f6f8fc;
  border: 1px solid $border;
  border-radius: 9px;
  width: 32px;
  height: 32px;
  cursor: pointer;
  color: $muted;
}
.mr-d__body {
  padding: 20px;
  overflow-y: auto;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.mr-d__body ::v-deep .mr-embed {
  position: relative;
  width: 100%;
  padding-top: 56.25%;
  border-radius: 12px;
  overflow: hidden;
  background: #000;
}
.mr-d__body ::v-deep .mr-embed iframe {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: 0;
}
.mr-d__body ::v-deep .mr-media-el {
  width: 100%;
  border-radius: 12px;
  display: block;
}
.mr-d__body ::v-deep .mr-rtext {
  font-size: 13.5px;
  color: $ink;
  line-height: 1.6;
  margin: 0;
}
.mr-d__body ::v-deep .mr-link {
  color: $teal;
  font-weight: 600;
  font-size: 13px;
  text-decoration: none;
}
.mr-d__body ::v-deep .mr-empty-content {
  color: $faint;
  font-size: 13px;
  text-align: center;
  padding: 40px 12px;
  font-style: italic;
}
.mr-d__foot {
  padding: 16px 20px;
  border-top: 1px solid $border;
}
.mr-btn {
  width: 100%;
  font: inherit;
  font-size: 13px;
  font-weight: 700;
  color: #fff;
  background: $teal;
  border: none;
  border-radius: 11px;
  padding: 12px;
  cursor: pointer;
}
</style>
